import { chain, omit, has } from 'lodash'
import React from 'react'
import type { MaterialBatchInvoice, OrderState, InvoiceActionCollection, OrderAction } from './types'

export const GetKey = (material: string, batch: string) => `${material}//${batch}`

export const AddMaterialBatchAction = (state: OrderState, invoices: MaterialBatchInvoice[], quantity: number, orderType: 'Return' | 'Exchange'): OrderState => {
    if (!invoices || !invoices.length) return state

    const actionableInvoices = invoices.filter(i => orderType === 'Return' ? i.return : i.exchange)

    if (!actionableInvoices) return state

    const key = GetKey(actionableInvoices[0].material, actionableInvoices[0].batch)
    const maxQuantity = actionableInvoices.reduce((sum, i) => sum + parseInt(i.quantity), 0)
    const q = quantity > maxQuantity ? maxQuantity : quantity
    
    return {
        ...state,
        [key]: {
            invoices: actionableInvoices,
            action: CalculateAction([], actionableInvoices, q),
            material: actionableInvoices[0].material,
            batch: actionableInvoices[0].batch,
            description: actionableInvoices[0].description,
            price: actionableInvoices[0].materialPrice,
            currency: actionableInvoices[0].materialPriceCurrency,
            reason: '_',
            quantity: q,
            maxQuantity
        }
    }
}

export const CalculateAction = (state: InvoiceActionCollection, invoices: MaterialBatchInvoice[], quantity: number): InvoiceActionCollection => {
    const orderedInvoices = chain(invoices)
        .filter((i) => !!i)
        .sortBy('invoiceNumber', 'invoiceLineItem')
        .reverse()
        .value()

    let total = 0
    let invoiceQuantity: Record<string, number> = {}

    for (const invoice of orderedInvoices) {
        if (total >= quantity) break
        const q = parseInt(invoice.quantity)
        total += q
        invoiceQuantity = { ...invoiceQuantity, [`${invoice.invoiceNumber}//${invoice.invoiceLineItem}`]: total >= quantity ? quantity - total + q : q }
    }

    return orderedInvoices
        .filter(i => has(invoiceQuantity, `${i.invoiceNumber}//${i.invoiceLineItem}`))
        .map(i => ({
            invoiceNumber: i.invoiceNumber,
            invoiceLineItem: i.invoiceLineItem,
            invoiceDate: i.invoiceDate,
            quantity: invoiceQuantity[`${i.invoiceNumber}//${i.invoiceLineItem}`]
        }))
}

export const ChangeQuantityAction = (state: OrderState, key: string, quantity: number | undefined, allowDelete: boolean): OrderState => {
    if (!state[key]) return state
    if (allowDelete && (quantity || 0) <= 0) return omit(state, key)

    return {
        ...state,
        [key]: {
            ...state[key],
            quantity: (quantity || 0) > state[key].maxQuantity ? state[key].maxQuantity : quantity,
            action: CalculateAction(state[key].action, state[key].invoices, (quantity || 0) > state[key].maxQuantity ? state[key].maxQuantity : (quantity || 0))
        }
    }
}

export const CleanOrderAction = (state: OrderState): OrderState => chain(state)
    .filter(o => (o.quantity || 0) > 0)
    .reduce((acc, mb) => ({...acc, [GetKey(mb.material, mb.batch)]: mb}), {})
    .value()

export const UpdateInvoiceReturnReasonAction = (state: OrderState, key: string, reason: string): OrderState => ({
    ...state,
    [key]: {
        ...state[key],
        reason
    }
})

export const UpdateInvoiceReturnPORefAction = (state: OrderState, key: string, poref: string): OrderState => ({
    ...state,
    [key]: {
        ...state[key],
        poref
    }
})

const OrderReducer: React.Reducer<OrderState, OrderAction> = (state = {}, action) => {
    switch (action.type) {
        case 'InitializeState':
            return action.state
        case 'AddMaterialBatch':
            return AddMaterialBatchAction(state, action.invoices, action.quantity, action.orderType)
        case 'CancelOrder':
            return {}
        case 'CleanOrder':
            return CleanOrderAction(state)
        default:
            const key = GetKey(action.material, action.batch)

            switch (action.type) {
                case 'RemoveMaterialBatch':
                    return omit(state, key)
                case 'ChangeQuantity':
                    return ChangeQuantityAction(state, key, action.quantity, !!action.allowDelete)
                case 'UpdateInvoiceReturnReason':
                    return UpdateInvoiceReturnReasonAction(state, key, action.reason)
                case 'UpdateInvoicePORef':
                    return UpdateInvoiceReturnPORefAction(state, key, action.poref)
                default:
                    return state
            }
    }
}

export default OrderReducer
